// Lib
import { createSelector } from 'reselect';
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';

// Selectors
import { getCurrentBoardCanvasOrder, getCurrentBoardChildren } from '../../element/selectors/currentBoardSelector';
import { breadcrumbSelector } from '../breadcrumbs/breadcrumbSelector';
import gridSizeSelector from '../../utils/grid/gridSizeSelector';

import { isLine } from '../../../common/elements/utils/elementTypeUtils';
import { sortByCreated, sortByScoreProperty } from '../../../common/elements/utils/elementSortUtils';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';

// Constants
import { BoardSections } from '../../../common/boards/boardConstants';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

const cleanElementMeta = (element) => element.delete('meta');

const canvasChildrenSelector = createShallowSelector(getCurrentBoardChildren, (children) =>
    children.filter((child) => child.getIn(['location', 'section']) === BoardSections.CANVAS),
);

const canvasChildrenForDocumentSelector = createShallowSelector(canvasChildrenSelector, (children) =>
    children.filterNot(isLine),
);

export const documentOrderedChildrenSelector = createShallowSelector(
    getCurrentBoardCanvasOrder,
    canvasChildrenForDocumentSelector,
    (order, children) => {
        // Any element that doesn't exist in the canvas order gets sorted by created date.
        // This is so that newly created elements from the canvas
        const orderedByCreatedElements = children
            .filter((element) => {
                const elementId = getElementId(element);
                return !order?.includes(elementId);
            })
            .toArray()
            .sort(sortByCreated)
            .map(cleanElementMeta)
            .reverse();

        const childrenWithoutMeta = children.map(cleanElementMeta);
        const canvasOrderedElements = (order ?? []).reduce((acc, elementId) => {
            const element = childrenWithoutMeta.get(elementId);
            if (element) acc.push(element);
            return acc;
        }, []);

        return [...orderedByCreatedElements, ...canvasOrderedElements];
    },
);

export const inboxChildrenSelector = createShallowSelector(getCurrentBoardChildren, (children) =>
    children
        .filter((child) => child.getIn(['location', 'section']) === BoardSections.INBOX)
        .sort(sortByScoreProperty)
        .toList(),
);

export const documentSelector = createSelector(
    gridSizeSelector,
    documentOrderedChildrenSelector,
    inboxChildrenSelector,
    breadcrumbSelector,
    getPlatformDetailsSelector,
    (gridSize, documentChildren, inboxChildren, breadcrumb, platformDetails) => ({
        orderedElements: documentChildren,
        inboxElements: inboxChildren,
        gridSize,
        breadcrumb,
        platformDetails,
    }),
);
